import { step, type GameContext } from "./state.ts";

export type LoopHandler = (ctx: GameContext, dtMin: number) => void;

export class GameLoop {
  private rafId: number | null = null;
  private lastTs = 0;
  private paused = false;
  private speed = 1; // 1x, 2x, 4x
  // 1 real-second = 1 game-minute at speed 1
  private readonly secondsPerGameMinute = 1;
  private onTick: LoopHandler | null = null;
  private onRender: ((ctx: GameContext) => void) | null = null;

  constructor(private ctx: GameContext) {}

  setContext(ctx: GameContext): void {
    this.ctx = ctx;
  }

  onUpdate(fn: LoopHandler): void { this.onTick = fn; }
  onFrame(fn: (ctx: GameContext) => void): void { this.onRender = fn; }

  start(): void {
    if (this.rafId !== null) return;
    this.lastTs = performance.now();
    const tick = (ts: number) => {
      const dtSec = Math.min(0.05, (ts - this.lastTs) / 1000); // cap to 50ms
      this.lastTs = ts;
      if (!this.paused) {
        const dtMin = (dtSec / this.secondsPerGameMinute) * this.speed;
        // Subdivide for stability (frequency dynamics)
        const sub = 4;
        for (let i = 0; i < sub; i++) step(this.ctx, dtMin / sub);
        if (this.onTick) this.onTick(this.ctx, dtMin);
      }
      if (this.onRender) this.onRender(this.ctx);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  togglePause(): boolean {
    this.paused = !this.paused;
    return this.paused;
  }

  isPaused(): boolean { return this.paused; }

  setSpeed(s: number): void { this.speed = s; }
  getSpeed(): number { return this.speed; }
}
